// Composables
import { useProxiedModel } from "../../composables/proxiedModel.js"; // Utilities
import { computed, ref, shallowRef, toRef, watch } from 'vue';
import { consoleError, createV0Locale, isString } from "../../util/index.js"; // Locales
import en from "../en.js"; // Types
const LANG_PREFIX = '$vuetify.';
const replace = (str, params) => {
  return str.replace(/\{(\d+)\}/g, (match, index) => {
    return String(params[Number(index)]);
  });
};
function selectLocale(bundle, locale) {
  if (!bundle.has(locale)) bundle.register({
    id: locale
  });
  if (bundle.selectedId.value !== locale) bundle.select(locale);
}
function createBundle(locale, fallbackLocale, catalogs) {
  const bundle = createV0Locale({
    fallback: fallbackLocale,
    messages: catalogs
  });
  selectLocale(bundle, locale);
  return bundle;
}
const createTranslateFunction = (current, fallback, messages) => {
  // keep computed instead of watch to avoid server-side memory leak
  // since createBundle writes reactive state it reads, we caching + hacks for computed to act like watch
  let lastEditCount = 0;
  const snapshot = computed(() => ({
    key: ++lastEditCount,
    current: current.value,
    fallback: fallback.value,
    messages: JSON.parse(JSON.stringify(messages.value)) // hack for deep tracking
  }));
  let cache;
  const bundle = computed(() => {
    const {
      key,
      current,
      fallback,
      messages
    } = snapshot.value;
    if (cache?.key !== key) {
      cache = {
        key,
        bundle: createBundle(current, fallback, messages)
      };
    }
    return cache.bundle;
  });
  return (key, ...params) => {
    if (!key.startsWith(LANG_PREFIX)) {
      return replace(key, params);
    }
    const found = bundle.value.ti(key.slice(LANG_PREFIX.length), ...params);
    if (!isString(found)) {
      consoleError(`Translation key "${key}" not found in fallback`);
      return key;
    }
    return found;
  };
};

// v0's n() drops Intl options, ignores the fallback locale, and skips Intl
// when there is no window. Pagination renders these numbers on the server.
function createNumberFunction(current, fallback) {
  return (value, options) => {
    const numberFormat = new Intl.NumberFormat([current.value, fallback.value], options);
    return numberFormat.format(value);
  };
}
function inferDecimalSeparator(current, fallback) {
  const format = createNumberFunction(current, fallback);
  return format(0.1).includes(',') ? ',' : '.';
}
function inferNumericGroupSeparator(current, fallback) {
  return new Intl.NumberFormat([current.value, fallback.value], {
    useGrouping: true
  }).formatToParts(10000).find(p => p.type === 'group')?.value ?? ' ';
}
function useProvided(props, prop, provided) {
  const internal = useProxiedModel(props, prop, props[prop] ?? provided.value);

  // TODO: Remove when defaultValue works
  internal.value = props[prop] ?? provided.value;
  watch(provided, v => {
    if (props[prop] == null) {
      internal.value = provided.value;
    }
  });
  return internal;
}
function createProvideFunction(state) {
  return props => {
    const current = useProvided(props, 'locale', state.current);
    const fallback = useProvided(props, 'fallback', state.fallback);
    const messages = useProvided(props, 'messages', state.messages);
    return {
      name: 'vuetify',
      current,
      fallback,
      messages,
      decimalSeparator: toRef(() => inferDecimalSeparator(current, fallback)),
      numericGroupSeparator: toRef(() => inferNumericGroupSeparator(current, fallback)),
      t: createTranslateFunction(current, fallback, messages),
      n: createNumberFunction(current, fallback),
      provide: createProvideFunction({
        current,
        fallback,
        messages
      })
    };
  };
}
export function createVuetifyAdapter(options) {
  const current = shallowRef(options?.locale ?? 'en');
  const fallback = shallowRef(options?.fallback ?? 'en');
  const messages = ref({
    en,
    ...options?.messages
  });
  return {
    name: 'vuetify',
    current,
    fallback,
    messages,
    decimalSeparator: toRef(() => options?.decimalSeparator ?? inferDecimalSeparator(current, fallback)),
    numericGroupSeparator: toRef(() => options?.numericGroupSeparator ?? inferNumericGroupSeparator(current, fallback)),
    t: createTranslateFunction(current, fallback, messages),
    n: createNumberFunction(current, fallback),
    provide: createProvideFunction({
      current,
      fallback,
      messages
    })
  };
}
//# sourceMappingURL=vuetify.js.map